import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { addDoc, collection, getDocs, doc, Timestamp, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { ShopifyShopModel, ShopifyShopStatus } from '../types/shopify';

export type ShopifyShop = ShopifyShopModel;


interface ShopifyShopState {
  shopifyShops: ShopifyShopModel[];
  selectedShopifyShop: ShopifyShopModel | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ShopifyShopState = {
  shopifyShops: [],
  selectedShopifyShop: null,
  status: 'idle',
  error: null,
};


const toShopifyShopModel = (data: any, id: string): ShopifyShopModel => {
  const shopify = data?.shopify || {};
  const accessLevels = data?.accessLevels || {};
  return {
    id,
    userIds: data?.userIds || Object.keys(accessLevels),
    shop: shopify.shop || data?.name || '',
    shopUrl: shopify.shopUrl,
    status: shopify.status || 'created',
    productCount: shopify.productCount ?? shopify.stats?.productCount,
    iamgeCount: shopify.imageCount ?? shopify.stats?.imageCount,
    llmLanguage: shopify.llmLanguage,
    llmKeywords: shopify.llmKeywords,
    llmTargetAudience: shopify.llmTargetAudience,
    llmDescription: shopify.llmDescription,
    shopifyTags: shopify.shopifyTags,
    shopifyVendors: shopify.shopifyVendors,
    shopifyProductTypes: shopify.shopifyProductTypes,
    stats: shopify.stats,
    updatedAt: data?.updatedAt,
    createdAt: data?.createdAt,
  } as ShopifyShopModel;
};

const shopifyFieldKeys = new Set([
  'shop',
  'shopUrl',
  'status',
  'productCount',
  'iamgeCount',
  'llmLanguage',
  'llmKeywords',
  'llmTargetAudience',
  'llmDescription',
  'shopifyTags',
  'shopifyVendors',
  'shopifyProductTypes',
  'stats',
]);

export const updateShopifyShop = createAsyncThunk(
  'shopifyShops/updateShopifyShop',
  async (updatedShopifyShop: Partial<ShopifyShopModel> & { id: string }, { rejectWithValue }) => {
    try {
      const { id, ...updates } = updatedShopifyShop;
      const timestamp = Timestamp.now();

      const shopifyUpdates: Record<string, any> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) return;
        if (shopifyFieldKeys.has(key)) {
          const fieldKey = key === 'iamgeCount' ? 'imageCount' : key;
          shopifyUpdates[`shopify.${fieldKey}`] = value;
        }
      });

      await updateDoc(doc(db, 'companies', id), {
        ...shopifyUpdates,
        'modules.shopify': true,
        updatedAt: timestamp,
      });

      return { id, ...updates, updatedAt: timestamp };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

// Thunk to add a new website to the Firestore database
export const addShopifyShop = createAsyncThunk(
  'shopifyShops/addShopifyShop',
  async (newWebsite: Omit<ShopifyShopModel, 'id' | 'createdAt' | 'updatedAt' | 'status'>, { rejectWithValue }) => {
    try {
      // Get the current Firestore Timestamp
      const timestamp = Timestamp.now();

      const shopifyShopWithTimestamps = {
        shop: newWebsite.shop,
        shopUrl: newWebsite.shopUrl,
        status: ShopifyShopStatus.CREATED,
        createdAt: timestamp,
        updatedAt: timestamp,
        llmLanguage: newWebsite.llmLanguage,
        llmKeywords: newWebsite.llmKeywords,
        llmTargetAudience: newWebsite.llmTargetAudience,
        llmDescription: newWebsite.llmDescription,
        shopifyTags: newWebsite.shopifyTags,
        shopifyVendors: newWebsite.shopifyVendors,
        shopifyProductTypes: newWebsite.shopifyProductTypes,
        productCount: newWebsite.productCount,
      }

      const accessLevels: Record<string, number> = {};
      (newWebsite.userIds || []).forEach((uid) => {
        accessLevels[uid] = 80;
      });

      const companyPayload = {
        name: newWebsite.shop || newWebsite.shopUrl,
        status: 'created',
        modules: { shopify: true },
        accessLevels,
        createdAt: timestamp,
        updatedAt: timestamp,
        shopify: shopifyShopWithTimestamps,
      };

      const docRef = await addDoc(collection(db, 'companies'), companyPayload);
      return { id: docRef.id, ...shopifyShopWithTimestamps };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

export const fetchShopifyShops = createAsyncThunk('shopifyShops/fetchShopifyShops', async () => {
  const q = query(collection(db, 'companies'), where('modules.shopify', '==', true));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => toShopifyShopModel(doc.data(), doc.id));
});

const shopifyShopsSlice = createSlice({
  name: 'shopifyShops',
  initialState,
  reducers: {
    setShopifyShops: (state, action: PayloadAction<ShopifyShopModel[]>) => {
      state.shopifyShops = action.payload;
    },
    setSelectedShopifyShop: (state, action: PayloadAction<ShopifyShopModel | null>) => {
      state.selectedShopifyShop = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShopifyShops.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchShopifyShops.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.shopifyShops = action.payload;
      })
      .addCase(fetchShopifyShops.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      }).addCase(updateShopifyShop.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.shopifyShops.findIndex(shopifyShop => shopifyShop.id === action.payload.id);
        if (index !== -1) {
          // Update the website in the state
          state.shopifyShops[index] = { ...state.shopifyShops[index], ...action.payload };
        }
      })
      .addCase(updateShopifyShop.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      });
  },
});

export const { setShopifyShops, setSelectedShopifyShop } = shopifyShopsSlice.actions;
export default shopifyShopsSlice.reducer;
